import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    Tooltip,
    Typography,
    Paper,
    Chip
} from "@mui/material";
import {
    ArrowForward,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    RestartAlt as ResetIcon,
    FitnessCenter as ExerciseIcon,
    AdminPanelSettings as PermissionsIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import Loader from "../componenets/Loader";
import { getExercise, resetToOriginal, deleteExercise, saveExercise } from "../services/ExcersiceService";
import DeleteExerciseDialog from "../componenets/exercises/DeleteExerciseDialog";
import { canDeleteExercise } from "../utils/exercisePermissions";
import { useCompany } from "../contexts/CompanyContext";
import EditPermissionsDialog from "../componenets/new_templates/EditPermissionsDialog";
import { useAuth } from "../contexts/AuthContext";
import VariantInfoAlert from "../componenets/exercises/VariantInfoAlert";
import ExerciseForm from "../componenets/exercises/ExerciseForm";
import { useExerciseForm } from "../hooks/useExerciseForm";
import { RTL } from "../cache/CacheRtl";
import { imageUrl } from "../utils/imageUrl";
import GroupAdminCompaniesField from "../componenets/groupadmin/GroupAdminCompaniesField";
import CompanyChips from "../componenets/administrators/CompanyChips";

// Helper under the group-admin company picker on the exercise edit page (shared to the chosen companies;
// empty keeps it private to the group admin's library).
const EXERCISE_COMPANIES_HELPER =
    "אופציונלי — התרגיל ישותף לחברות שתבחר; ניתן להשאיר ריק ולשמור אותו בספרייה שלך בלבד";

/**
 * ExercisePage - View and edit exercise details
 * Uses ExerciseForm component and useExerciseForm hook for form handling
 */
const ExercisePage = () => {
    const { exerciseId } = useParams();
    const navigate = useNavigate();
    const { isSuperAdmin, isCompanyAdmin, isCompanyGroupAdmin } = useAuth();
    const { company } = useCompany();

    // Page state
    const [exercise, setExercise] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Use the shared form hook
    const form = useExerciseForm(isEditing ? exercise : null, {
        onSaveSuccess: (savedExercise) => {
            // Check if a new variant was created (ID changed)
            const wasVariantCreated = savedExercise.id !== exercise.id;

            if (wasVariantCreated) {
                setSnackbar({
                    open: true,
                    message: 'נוצרה גרסה מותאמת בהצלחה!',
                    severity: 'success'
                });
                navigate(`/app/exercises/${savedExercise.id}`);
            } else {
                setExercise(savedExercise);
                setIsEditing(false);
                setSnackbar({
                    open: true,
                    message: 'התרגיל נשמר בהצלחה!',
                    severity: 'success'
                });
            }
        },
        onSaveError: (error) => {
            console.error("Error updating exercise:", error);
            setSnackbar({
                open: true,
                message: 'שגיאה בשמירת התרגיל',
                severity: 'error'
            });
        }
    });

    // Fetch exercise on mount
    useEffect(() => {
        if (exerciseId) {
            fetchExercise(exerciseId);
        }
    }, [exerciseId]);

    const fetchExercise = async (id) => {
        try {
            setLoading(true);
            // A COMPANY_GROUP_ADMIN's library lists System exercises alongside their own, but the
            // owner-scoped GET (/groupAdmin/exercise/{id}) only resolves their own — opening a System
            // exercise there 404s ("Failed to load"). Fetch from the library endpoint instead so both
            // System and own exercises resolve (System ones render read-only, see canEditExercise).
            const response = await getExercise(id, isCompanyGroupAdmin());
            setExercise(response.data);
            setError(null);
        } catch (err) {
            console.error("Error fetching exercise:", err);
            setError("Failed to load exercise details");
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate("/app/exercises");
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        if (form.hasChanges) {
            setShowCancelDialog(true);
        } else {
            cancelEdit();
        }
    };

    const cancelEdit = () => {
        form.resetForm();
        setIsEditing(false);
        setShowCancelDialog(false);
    };

    const handleSave = async () => {
        if (form.isUploading) {
            setSnackbar({
                open: true,
                message: 'יש להמתין לסיום העלאת הקבצים',
                severity: 'warning'
            });
            return;
        }

        const result = await form.handleSave();
        if (!result.success && result.error === 'Validation failed') {
            setSnackbar({
                open: true,
                message: 'אנא מלא את כל השדות הנדרשים',
                severity: 'error'
            });
        }
    };

    const handleReset = () => {
        if (!window.confirm('האם אתה בטוח שברצונך לאפס את התרגיל למקור? פעולה זו תמחק את ההתאמות האישיות.')) {
            return;
        }

        setLoading(true);
        resetToOriginal(exercise.id)
            .then(() => {
                setSnackbar({
                    open: true,
                    message: 'התרגיל אופס למקור בהצלחה',
                    severity: 'success'
                });
                handleGoBack();
            })
            .catch(e => {
                console.log(e);
                setSnackbar({
                    open: true,
                    message: 'שגיאה באיפוס התרגיל',
                    severity: 'error'
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // A COMPANY_GROUP_ADMIN owns a private library that also surfaces the System Administrator's
    // exercises. They may view+edit their own (systemResource=false) but get read-only access to
    // System exercises — hide the edit action so they can't fork/alter them.
    const canEditExercise = () => !(isCompanyGroupAdmin() && exercise?.systemResource);

    const canDelete = () => canDeleteExercise(exercise, {
        isSuperAdmin: isSuperAdmin(),
        isCompanyAdmin: isCompanyAdmin(),
        companyId: company?.id,
    });

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteExercise(exercise.id);
            setSnackbar({
                open: true,
                message: 'התרגיל נמחק בהצלחה',
                severity: 'success'
            });
            // Navigate back to exercise list after successful deletion
            navigate('/app/exercises');
        } catch (error) {
            console.error('Error deleting exercise:', error);
            const errorMessage = error.response?.data || 'שגיאה במחיקת התרגיל';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    };

    // Permission helpers
    const canManagePermissions = () => {
        if (!exercise?.id) return false;
        if (exercise.systemResource && !isSuperAdmin()) return false;
        if (exercise.variant && !isSuperAdmin()) return false;
        return isSuperAdmin();
    };

    const handlePermissionsSave = async (updatedExercise) => {
        try {
            if (exercise.systemResource && !isSuperAdmin()) {
                throw new Error("רק מנהל מערכת יכול לערוך הרשאות של תרגילי מערכת");
            }
            // Persist to the server — the dialog produces the new companyPermissions
            // (null = all companies, [] = system-admin only, [...] = specific), and the server
            // rewrites the resource_authorizations rows accordingly. Previously this only updated
            // local state, so the permission change was silently discarded on reload and the
            // exercise stayed invisible to every company.
            const { data: savedExercise } = await saveExercise(updatedExercise);
            setExercise(savedExercise);
            setPermissionsDialogOpen(false);
            setSnackbar({
                open: true,
                message: 'ההרשאות עודכנו בהצלחה!',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating permissions:', error);
            setSnackbar({
                open: true,
                message: 'שגיאה בשמירת ההרשאות',
                severity: 'error'
            });
            throw error;
        }
    };

    const getPermissionsDisplayText = () => {
        if (!exercise?.companyPermissions) return 'כל החברות';

        if (Array.isArray(exercise.companyPermissions)) {
            if (exercise.companyPermissions.length === 0) return 'מנהל בלבד';
            return `${exercise.companyPermissions.length} חברות`;
        }
        return null;
    };

    const getSaveTooltip = () => {
        if (!form.hasChanges) return "אין שינויים לשמירה";
        if (exercise?.systemResource && !isSuperAdmin() && !exercise?.variant) {
            return "שמור שינויים (ייווצר עותק מותאם לחברה שלך)";
        }
        return "שמור שינויים";
    };

    const canSave = form.isValid &&
        form.hasChanges &&
        !form.nameExistsError &&
        !form.nameHebExistsError &&
        !form.isCheckingName &&
        !form.isCheckingNameHeb &&
        !form.isUploading &&
        !form.isSaving;

    // Loading state
    if (loading) {
        return <Loader loading={true} />;
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ padding: "20px", textAlign: "center" }}>
                <Typography variant="h6" color="error" gutterBottom>
                    {error}
                </Typography>
                <Button onClick={handleGoBack} variant="contained">
                    חזור לרשימת התרגילים
                </Button>
            </Box>
        );
    }

    // Not found state
    if (!exercise) {
        return (
            <Box sx={{ padding: "20px", textAlign: "center" }}>
                <Typography variant="h6" gutterBottom>
                    תרגיל לא נמצא
                </Typography>
                <Button onClick={handleGoBack} variant="contained">
                    חזור לרשימת התרגילים
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Loader loading={loading || form.isSaving} />

            {/* Header with Back Button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, pt: 2, mb: 2 }}>
                <IconButton
                    onClick={handleGoBack}
                    sx={{
                        color: "#64748B",
                        "&:hover": {
                            backgroundColor: "#F1F5F9",
                            color: "#1E293B"
                        }
                    }}
                >
                    <ArrowForward />
                </IconButton>
                <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1E293B" }}>
                    פרטי תרגיל
                </Typography>
            </Box>

            {/* Main Content */}
            <Box sx={{ px: 3 }}>
                {/* Header Card with Actions */}
                <Paper
                    sx={{
                        backgroundColor: "#FCFCFD",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        mb: 3
                    }}
                    elevation={0}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            backgroundColor: "#F8FAFC",
                            borderBottom: isEditing ? "1px solid #E2E8F0" : "none",
                            borderRadius: isEditing ? "12px 12px 0 0" : "12px"
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <ExerciseIcon sx={{ color: "#64748B", fontSize: "20px" }} />
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                {isEditing ? 'עריכת תרגיל' : exercise.nameHeb || exercise.name}
                            </Typography>
                            {exercise?.systemResource && (
                                <Chip
                                    label="תרגיל מערכת"
                                    size="small"
                                    sx={{
                                        backgroundColor: "#DBEAFE",
                                        color: "#1E40AF",
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        height: "18px"
                                    }}
                                />
                            )}
                            {exercise?.variant && (
                                <Chip
                                    label="גרסה מותאמת"
                                    size="small"
                                    sx={{
                                        backgroundColor: "#EFF6FF",
                                        color: "#1E40AF",
                                        border: "1px solid #BFDBFE",
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        height: "18px"
                                    }}
                                />
                            )}
                            {exercise?.ownerUserId && (
                                <Chip
                                    label="מנהל קבוצת חברות"
                                    size="small"
                                    sx={{
                                        backgroundColor: "#FEF3C7",
                                        color: "#92400E",
                                        border: "1px solid #FDE68A",
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        height: "18px"
                                    }}
                                />
                            )}
                            {isSuperAdmin() && getPermissionsDisplayText() && (
                                <RTL>
                                    <Chip
                                        icon={<PermissionsIcon sx={{ fontSize: "10px !important" }} />}
                                        label={getPermissionsDisplayText()}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#FEF3C7",
                                            color: "#D97706",
                                            fontSize: "10px",
                                            height: "18px"
                                        }}
                                    />
                                </RTL>
                            )}
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            {isEditing ? (
                                <>
                                    {/* Permissions Button */}
                                    {canManagePermissions() && (
                                        <Tooltip title="ניהול הרשאות">
                                            <IconButton
                                                onClick={() => setPermissionsDialogOpen(true)}
                                                sx={{
                                                    color: "#64748B",
                                                    "&:hover": {
                                                        backgroundColor: "#FEF3C7",
                                                        color: "#D97706"
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <PermissionsIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {/* Reset button - only for variants */}
                                    {exercise?.variant && (
                                        <Tooltip title="איפוס למקור - מחזיר את התרגיל לגרסה המקורית">
                                            <IconButton
                                                onClick={handleReset}
                                                disabled={form.isSaving}
                                                sx={{
                                                    color: "#DC2626",
                                                    "&:hover": {
                                                        backgroundColor: "#FEF2F2",
                                                        color: "#B91C1C"
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <ResetIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    <Tooltip title={getSaveTooltip()}>
                                        <span>
                                            <IconButton
                                                onClick={handleSave}
                                                disabled={!canSave}
                                                sx={{
                                                    color: canSave ? "#22C55E" : "#64748B",
                                                    "&:hover": {
                                                        backgroundColor: "#DCFCE7",
                                                        color: "#16A34A"
                                                    },
                                                    "&:disabled": {
                                                        color: "#CBD5E1"
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title="ביטול עריכה">
                                        <IconButton
                                            onClick={handleCancelClick}
                                            disabled={form.isSaving}
                                            sx={{
                                                color: "#EF4444",
                                                "&:hover": {
                                                    backgroundColor: "#FEE2E2",
                                                    color: "#DC2626"
                                                }
                                            }}
                                            size="small"
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                <>
                                    {canDelete() && (
                                        <Tooltip title="מחק תרגיל">
                                            <IconButton
                                                onClick={() => setShowDeleteDialog(true)}
                                                sx={{
                                                    color: "#EF4444",
                                                    "&:hover": {
                                                        backgroundColor: "#FEE2E2",
                                                        color: "#DC2626"
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canEditExercise() && (
                                        <Tooltip title={
                                            exercise?.systemResource && !isSuperAdmin()
                                                ? "עריכת תרגיל (ייווצר עותק מותאם לחברה שלך)"
                                                : "עריכת תרגיל"
                                        }>
                                            <IconButton
                                                onClick={handleEditClick}
                                                sx={{
                                                    color: "#64748B",
                                                    "&:hover": {
                                                        backgroundColor: "#E2E8F0",
                                                        color: "#475569"
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Form Content - Only shown in edit mode */}
                    {isEditing && (
                        <Box sx={{ p: 3 }}>
                            <ExerciseForm
                                formValues={form.formValues}
                                setFieldValue={form.setFieldValue}
                                setFieldTouched={form.setFieldTouched}
                                uploadStates={form.uploadStates}
                                isUploading={form.isUploading}
                                imagePreview={form.imagePreview}
                                coverPreview={form.coverPreview}
                                isVideoUploadMode={form.isVideoUploadMode}
                                setIsVideoUploadMode={form.setIsVideoUploadMode}
                                videoFile={form.videoFile}
                                videoFileName={form.videoFileName}
                                videoPreviewUrl={form.videoPreviewUrl}
                                existingVideoUrl={form.existingVideoUrl}
                                handleImageUpload={form.handleImageUpload}
                                handleCoverExtracted={form.handleCoverExtracted}
                                handleVideoFileChange={form.handleVideoFileChange}
                                handleVideoUrlChange={form.handleVideoUrlChange}
                                handleRemoveVideo={form.handleRemoveVideo}
                                errors={form.errors}
                                touched={form.touched}
                                nameExistsError={form.nameExistsError}
                                nameHebExistsError={form.nameHebExistsError}
                                isCheckingName={form.isCheckingName}
                                isCheckingNameHeb={form.isCheckingNameHeb}
                                checkNameExists={form.checkNameExists}
                                checkNameHebExists={form.checkNameHebExists}
                                exercise={exercise}
                                isSuperAdmin={isSuperAdmin()}
                                mode="page"
                            />

                            {/* A Company Group Administrator editing their OWN exercise can re-target which
                                authorized companies it is shared to (a subset, like a workout program).
                                canEditExercise() is false for System exercises, so this only shows for
                                exercises the group admin owns. */}
                            {isCompanyGroupAdmin() && canEditExercise() && (
                                <Box sx={{ mt: 3 }}>
                                    <GroupAdminCompaniesField
                                        value={form.formValues.companyPermissions}
                                        onChange={(companies) => form.setFieldValue('companyPermissions', companies)}
                                        emptyHelperText={EXERCISE_COMPANIES_HELPER}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>

                {/* View Mode - Display exercise details */}
                {!isEditing && (
                    <ExerciseViewMode
                        exercise={exercise}
                        showCompanies={isCompanyGroupAdmin() && !!exercise?.ownerUserId}
                    />
                )}
            </Box>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                dir="rtl"
            >
                <DialogTitle>ביטול עריכה</DialogTitle>
                <DialogContent>
                    <Typography>
                        יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לבטל את העריכה?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCancelDialog(false)}>
                        המשך עריכה
                    </Button>
                    <Button onClick={cancelEdit} color="error">
                        בטל שינויים
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Permissions Dialog */}
            <EditPermissionsDialog
                open={permissionsDialogOpen}
                onClose={() => setPermissionsDialogOpen(false)}
                onSave={handlePermissionsSave}
                template={exercise}
            />

            {/* Delete Exercise Dialog */}
            <DeleteExerciseDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
                exercise={exercise}
                isLoading={isDeleting}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

/**
 * ExerciseViewMode - Read-only view of exercise details
 */
const ExerciseViewMode = ({ exercise, showCompanies = false }) => {
    const { targetMuscles } = require("../enums/TargetMuscles");
    const { resistanceTypes } = require("../enums/resistanceTypes");

    const assignedCompanies = (exercise.companyPermissions || [])
        .map((c) => ({ id: c.companyId, name: c.companyName }));

    const getMuscleName = (value) => {
        const muscle = targetMuscles.find(m => m.value === value);
        return muscle ? muscle.name : value || 'לא נבחר';
    };

    const getResistanceName = (value) => {
        const type = resistanceTypes.find(t => t.value === value);
        return type ? type.name : value || 'לא נבחר';
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Info Alert */}
            <VariantInfoAlert exercise={exercise} isEditing={false} />

            {/* Basic Info */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    backgroundColor: '#FCFCFD',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px'
                }}
            >
                <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1E293B', mb: 2 }}>
                    מידע בסיסי
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <InfoField label="שם התרגיל (עברית)" value={exercise.nameHeb} />
                    <InfoField label="שם התרגיל (אנגלית)" value={exercise.name} />
                    <InfoField label="שריר ראשי" value={getMuscleName(exercise.targetMuscle)} />
                    <InfoField label="סוג התנגדות" value={getResistanceName(exercise.resistanceType)} />
                    {exercise.catalyst && (
                        <InfoField label="תרגיל מבוסס זמן" value="כן" />
                    )}
                    <InfoField
                        label="טווח חזרות ברירת מחדל"
                        value={exercise.defaultMinReps && exercise.defaultMaxReps
                            ? `${exercise.defaultMinReps} - ${exercise.defaultMaxReps}`
                            : null}
                    />
                </Box>
            </Paper>

            {/* Company Group Administrators see which authorized companies this exercise is shared to. */}
            {showCompanies && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        backgroundColor: '#FCFCFD',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px'
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1E293B', mb: 2 }}>
                        חברות משויכות
                    </Typography>
                    {assignedCompanies.length > 0 ? (
                        <CompanyChips companies={assignedCompanies} max={6} />
                    ) : (
                        <Typography sx={{ fontSize: '13px', color: '#64748B' }}>
                            התרגיל אינו משויך לחברות — נשמר בספרייה שלך בלבד
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Description */}
            {(exercise.descriptionHeb || exercise.description) && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        backgroundColor: '#FCFCFD',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px'
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1E293B', mb: 2 }}>
                        תיאור
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        {exercise.descriptionHeb && (
                            <InfoField label="תיאור (עברית)" value={exercise.descriptionHeb} multiline />
                        )}
                        {exercise.description && (
                            <InfoField label="תיאור (אנגלית)" value={exercise.description} multiline />
                        )}
                    </Box>
                </Paper>
            )}

            {/* Media */}
            {(exercise.imageUrl || exercise.coverUrl || exercise.videoUrl) && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        backgroundColor: '#FCFCFD',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px'
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1E293B', mb: 2 }}>
                        מדיה
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {exercise.imageUrl && (
                            <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#64748B', mb: 1 }}>
                                    תמונה ראשית
                                </Typography>
                                <img
                                    src={imageUrl(exercise.imageUrl, 200)}
                                    alt="תמונה ראשית"
                                    style={{
                                        width: '200px',
                                        height: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #E2E8F0'
                                    }}
                                />
                            </Box>
                        )}
                        {exercise.coverUrl && (
                            <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#64748B', mb: 1 }}>
                                    תמונת כיסוי
                                </Typography>
                                <img
                                    src={imageUrl(exercise.coverUrl, 400)}
                                    alt="תמונת כיסוי"
                                    style={{
                                        width: '400px',
                                        maxWidth: '100%',
                                        height: '150px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid #E2E8F0'
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                    {exercise.videoUrl && (
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#64748B', mb: 1 }}>
                                סרטון
                            </Typography>
                            {exercise.videoUrl.includes('cloudfront.net') || exercise.videoUrl.includes('amazonaws.com') ? (
                                <video
                                    controls
                                    style={{
                                        width: '100%',
                                        maxWidth: '600px',
                                        maxHeight: '400px',
                                        borderRadius: '8px'
                                    }}
                                    src={exercise.videoUrl}
                                >
                                    הדפדפן שלך לא תומך בתגית וידאו.
                                </video>
                            ) : (
                                <Typography sx={{ fontSize: '14px', color: '#475569', wordBreak: 'break-all' }}>
                                    {exercise.videoUrl}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

/**
 * InfoField - Simple read-only field display
 */
const InfoField = ({ label, value, multiline = false }) => (
    <Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#64748B', mb: 0.5 }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: '14px',
                color: '#1E293B',
                whiteSpace: multiline ? 'pre-line' : 'normal'
            }}
        >
            {value || 'לא צוין'}
        </Typography>
    </Box>
);

export default ExercisePage;
